import { z } from 'zod';

import { createPaginatedResultSchema } from './shared/paginated';

export const GeoTag = z.object({
  lat: z.number(),
  lon: z.number(),
});

export const CameraInfo = z.object({
  SerialNumber: z.string().min(1),
  ProdShortName: z.string().min(1),
  MACAddress: z.string().min(1).optional(),
  type: z.string().optional(),
});

export const CameraEvent = z.object({
  eventId: z.string(),
  plate: z.string(),
  plateState: z.string().optional(),
  capturedAtUnix: z.number(),
  plateConfidence: z.number(),
  packetCounter: z.string(),
  geoTag: GeoTag,
  cameraInfo: CameraInfo,
  imagePath: z.string(),
  plateImageType: z.enum(['png', 'jpeg', 'bmp']).or(z.string()),
  imageType: z.string(),
  moveDirection: z.enum(['in', 'out', 'unknown', 'any']),
  locationID: z.string(),
  croppedImagePath: z.string(),
  plateCoordinates: z.tuple([z.number(), z.number(), z.number(), z.number()]),
  plateCoordinatesRelative: z.tuple([
    z.number(),
    z.number(),
    z.number(),
    z.number(),
  ]),
  isConfirmed: z.boolean(),
  blockFaceId: z.string().uuid().nullish(),
});

export type CameraEventType = z.infer<typeof CameraEvent>;
export const CameraEventList = z.array(CameraEvent);
export type CameraEventListType = z.infer<typeof CameraEventList>;

export const CameraEventsPaginatedResult = createPaginatedResultSchema(
  CameraEvent,
  z.object({
    eventId: z.string(),
    capturedAtUnix: z.number(),
  })
);
export type CameraEventsPaginatedResultType = z.infer<
  typeof CameraEventsPaginatedResult
>;
export type GeoTagType = z.infer<typeof GeoTag>;
export type CameraInfoType = z.infer<typeof CameraInfo>;

/**
 * Schema for the camera event confirmation API request.
 * Maps portal field names to API-expected names:
 * - `locationID` → `locationId`
 * - `moveDirection` → `direction`
 */
export const ConfirmCameraEventRequest = z.object({
  eventId: z.string(),
  plate: z.string(),
  plateState: z.string().optional(),
  locationId: z.string(),
  direction: z.enum(['in', 'out', 'unknown', 'any']),
});

export type ConfirmCameraEventRequestType = z.infer<
  typeof ConfirmCameraEventRequest
>;
